import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useRouteParams} from '@github-ui/react-core/future/use-route-params'
import {useSuspenseRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {useQueries} from '@github-ui/react-query'
import {useMemo} from 'react'

import {repoPullsDeferredQueryFn, repoPullsToListItems} from '../helpers/repo-pulls-dashboard-proto'
import {repoStackDetailRoute} from '../routes/repo-stack-detail-route'

// The repository deferred endpoint accepts at most 25 PR IDs per request.
const BATCH_SIZE = 25
const DEFERRED_STALE_TIME = 30_000

export function useStackPullRequestMetadata() {
  const {owner, repo} = useRouteParams(repoStackDetailRoute)
  const {data} = useSuspenseRouteQuery(repoStackDetailRoute, 'mainQuery')
  const enabled = isFeatureEnabled('pull_requests_stacks_view_ui_checks')
  const batches = useMemo(() => {
    const result: string[][] = []
    if (enabled && data.stack) {
      for (let index = 0; index < data.pullRequests.length; index += BATCH_SIZE) {
        result.push(data.pullRequests.slice(index, index + BATCH_SIZE).map(pull => String(pull.id)))
      }
    }
    return result
  }, [data.stack, data.pullRequests, enabled])
  const queries = useQueries({
    queries: batches.map((ids, index) => ({
      queryKey: [
        'pull-requests',
        'stack-pull-request-metadata',
        owner,
        repo,
        ids,
        data.pullRequests
          .slice(index * BATCH_SIZE, (index + 1) * BATCH_SIZE)
          .map(pull => [pull.headSha, pull.updatedAt]),
      ],
      queryFn: ({signal}: {signal: AbortSignal}) =>
        repoPullsDeferredQueryFn({
          routeId: 'repoPullsDashboardDeferredRoute',
          queryName: 'deferred',
          queryDeps: {
            pathname: `/${owner}/${repo}/pulls/_deferred`,
            searchParams: ids.map<[string, string]>(id => ['pr_ids[]', id]),
            init: {signal},
          },
        }),
      staleTime: DEFERRED_STALE_TIME,
    })),
    combine: results => ({
      deferredResults: results.flatMap(query => (query.data?.error ? [] : (query.data?.results ?? []))),
      pendingBatches: results.map(query => query.isPending),
    }),
  })
  const {deferredResults, pendingBatches} = queries
  const pulls = useMemo(
    () => repoPullsToListItems(data.pullRequests, deferredResults),
    [data.pullRequests, deferredResults],
  )
  const pendingIds = new Set(batches.flatMap((ids, index) => (pendingBatches[index] ? ids : [])))

  return {pulls, pendingIds}
}
